import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Typography,
  Button,
} from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { FileUpload } from "@mui/icons-material";
import { ExcelImportDialog } from "../details/components/excel-import/ExcelImportDialog";
import { useModal } from "@refinedev/core";

interface CountryOption {
  countryId?: number;
  countryName?: string;
  countryFlagUrl?: string;
}
interface CarrierOption {
  carrierId?: number;
  carrierName?: string;
}

interface ShipmentFiltersProps {
  countries: CountryOption[];
  carriers: CarrierOption[];
  selectedCountry?: number;
  selectedCarrier?: number;
  onCountryChange: (_?: number) => void;
  onCarrierChange: (_?: number) => void;
}

export const ShipmentFilters = ({
  countries,
  carriers,
  selectedCountry,
  selectedCarrier,
  onCountryChange,
  onCarrierChange,
}: ShipmentFiltersProps) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.shipmentPrices.fields"
  );

  const {
    show: showImportModal,
    visible: isImportVisible,
    close: closeImportModal,
  } = useModal();

  return (
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.customBackground.box,
        borderRadius: 1,
        p: 1,
        my: 2,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
      }}
    >
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>{translate("country")}</InputLabel>
        <Select
          value={selectedCountry ?? ""}
          label={translate("country")}
          onChange={(e) =>
            onCountryChange(
              e.target.value === "" ? undefined : Number(e.target.value)
            )
          }
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {countries.map((country) => (
            <MenuItem key={country.countryId} value={country.countryId}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  src={country.countryFlagUrl}
                  alt={country.countryName}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography variant="body2">
                  {country.countryName?.startsWith("country_id")
                    ? country.countryName.split("country_id")[1]
                    : country.countryName}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>{translate("carrier")}</InputLabel>
        <Select
          value={selectedCarrier ?? ""}
          label={translate("carrier")}
          variant="outlined"
          onChange={(e) =>
            onCarrierChange(
              e.target.value === "" ? undefined : Number(e.target.value)
            )
          }
          disabled={!selectedCountry}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {carriers.map((carrier) => (
            <MenuItem key={carrier.carrierId} value={carrier.carrierId}>
              <Typography variant="body2">{carrier.carrierName}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {(!!selectedCountry || !!selectedCarrier) && (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => {
            onCountryChange(undefined);
            onCarrierChange(undefined);
          }}
        >
          {translateWithoutPrefix("buttons.clear")}
        </Button>
      )}
      <Box flexGrow={1} />
      <Button
        variant="outlined"
        startIcon={<FileUpload />}
        color="primary"
        onClick={showImportModal}
      >
        {translateWithoutPrefix("pages.shipmentPrices.details.importExcel")}
      </Button>
      <ExcelImportDialog open={isImportVisible} onClose={closeImportModal} />
    </Box>
  );
};
